import { useState, useEffect, useCallback } from 'react';
import api, { formatoMXN } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Users, Search, Gift, History, Trash2, Phone, Car, Plus, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

export default function CarwashClientes() {
  const [clientes, setClientes] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [cargando, setCargando] = useState(true);
  const [detalle, setDetalle] = useState(null);
  const [lealtadMeta, setLealtadMeta] = useState(5);
  const [dialogNuevo, setDialogNuevo] = useState(false);
  const [creando, setCreando] = useState(false);
  const [form, setForm] = useState({ nombre: '', telefono: '', placa: '', tipo_vehiculo: 'auto', color: '', marca: '', modelo: '', anio: '', notas: '' });
  const [vehiculoEdit, setVehiculoEdit] = useState(null);
  const [dialogVeh, setDialogVeh] = useState(false);
  const [formVeh, setFormVeh] = useState({ placa: '', tipo_vehiculo: 'auto', color: '', marca: '', modelo: '', anio: '', notas: '' });
  const [guardandoVeh, setGuardandoVeh] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const [c, cfg] = await Promise.all([
        api.get('/carwash/clientes', { params: busqueda ? { q: busqueda } : {} }),
        api.get('/configuracion'),
      ]);
      setClientes(c.data);
      setLealtadMeta(cfg.data?.lealtad_visitas_premio || 5);
    } catch { /* silent */ }
    finally { setCargando(false); }
  }, [busqueda]);

  useEffect(() => { cargar(); }, [cargar]);

  const verDetalle = async (cli) => {
    try {
      const res = await api.get(`/carwash/clientes/${cli.id}`);
      setDetalle(res.data);
    } catch { toast.error('Error al cargar detalle'); }
  };

  const eliminarCliente = async (id) => {
    if (!window.confirm('Eliminar este cliente? Su historial se conservara pero quedara sin vincular.')) return;
    try {
      await api.delete(`/carwash/clientes/${id}`);
      toast.success('Cliente eliminado');
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const crearCliente = async () => {
    if (!form.nombre.trim()) { toast.error('El nombre es obligatorio'); return; }
    if (form.telefono.length !== 10) { toast.error('El telefono debe tener 10 digitos'); return; }
    setCreando(true);
    try {
      await api.post('/carwash/clientes', form);
      toast.success('Cliente creado');
      setDialogNuevo(false);
      setForm({ nombre: '', telefono: '', placa: '', tipo_vehiculo: 'auto', color: '', marca: '', modelo: '', anio: '', notas: '' });
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al crear cliente'); }
    finally { setCreando(false); }
  };

  const guardarVehiculo = async () => {
    if (!detalle?.cliente?.id) return;
    setGuardandoVeh(true);
    try {
      if (vehiculoEdit) {
        await api.put(`/carwash/vehiculos/${vehiculoEdit.id}`, formVeh);
        toast.success('Vehículo actualizado');
      } else {
        if (!formVeh.placa.trim()) { toast.error('La placa es obligatoria'); setGuardandoVeh(false); return; }
        await api.post(`/carwash/clientes/${detalle.cliente.id}/vehiculos`, formVeh);
        toast.success('Vehículo agregado');
      }
      setDialogVeh(false);
      setVehiculoEdit(null);
      const res = await api.get(`/carwash/clientes/${detalle.cliente.id}`);
      setDetalle(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar vehículo'); }
    finally { setGuardandoVeh(false); }
  };

  const eliminarVehiculo = async (id) => {
    if (!window.confirm('¿Eliminar este vehículo?')) return;
    try {
      await api.delete(`/carwash/vehiculos/${id}`);
      toast.success('Vehículo eliminado');
      setDialogVeh(false);
      setVehiculoEdit(null);
      const res = await api.get(`/carwash/clientes/${detalle.cliente.id}`);
      setDetalle(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="carwash-clientes-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50 flex items-center gap-3">
          <Users className="w-8 h-8 text-cyan-400" /> Clientes
        </h1>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por nombre o telefono"
              className="bg-slate-950 border-slate-700 text-slate-200 pl-9"
              data-testid="buscar-cliente-input" />
          </div>
          <Button onClick={() => setDialogNuevo(true)} className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded-sm"
            data-testid="nuevo-cliente-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Nuevo Cliente
          </Button>
        </div>
      </div>

      <Card className="bg-slate-900/40 border-slate-800">
        <CardHeader className="pb-3">
          <CardTitle className="font-barlow text-lg text-slate-200">
            Base de Clientes ({clientes.length})
          </CardTitle>
          <p className="text-xs text-slate-500">
            Los clientes se registran automaticamente al crear un turno con telefono.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {cargando ? (
            <div className="p-12 text-center text-slate-500">Cargando...</div>
          ) : clientes.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-slate-700 mx-auto mb-3" />
              <p className="text-slate-500">{busqueda ? 'Sin resultados' : 'Aun no hay clientes registrados'}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Cliente</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Telefono</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-center">Visitas</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-center">Lealtad</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Total Gastado</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clientes.map(c => {
                  const contador = c.visitas_premio || 0;
                  const proxima = contador + 1;
                  const esPremio = proxima >= lealtadMeta;
                  return (
                    <TableRow key={c.id} className="border-slate-800/50" data-testid={`cliente-row-${c.id}`}>
                      <TableCell className="text-sm text-slate-200 font-medium">{c.nombre || '— Sin nombre —'}</TableCell>
                      <TableCell className="text-sm text-slate-400 font-mono">{c.telefono}</TableCell>
                      <TableCell className="text-center text-sm text-slate-300 font-mono">{c.total_visitas || 0}</TableCell>
                      <TableCell className="text-center">
                        {esPremio ? (
                          <Badge className="bg-amber-500/20 text-amber-400 border-amber-700 text-[10px]">
                            <Gift className="w-3 h-3 mr-1 inline" /> {proxima}/{lealtadMeta} PREMIO
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-800/60 text-slate-400 border-slate-700 text-[10px]">
                            {contador}/{lealtadMeta}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm text-emerald-400 font-mono">{formatoMXN(c.total_gastado || 0)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => verDetalle(c)}
                            className="text-slate-400 hover:text-cyan-400 h-8 w-8" data-testid={`ver-cliente-${c.id}`}>
                            <History className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => eliminarCliente(c.id)}
                            className="text-slate-400 hover:text-red-400 h-8 w-8" data-testid={`eliminar-cliente-${c.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!detalle} onOpenChange={() => setDetalle(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          {detalle && (
            <>
              <DialogHeader>
                <DialogTitle className="font-barlow text-xl text-slate-100">
                  {detalle.cliente.nombre || 'Cliente sin nombre'}
                </DialogTitle>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <Phone className="w-3 h-3" /> {detalle.cliente.telefono}
                  {detalle.cliente.placas?.length > 0 && (
                    <>
                      <Car className="w-3 h-3 ml-2" />
                      {detalle.cliente.placas.join(', ')}
                    </>
                  )}
                </div>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-slate-800/40 p-3 rounded-sm">
                    <p className="text-[10px] uppercase text-slate-500">Total Visitas</p>
                    <p className="text-lg font-bold text-cyan-400 font-mono">{detalle.cliente.total_visitas || 0}</p>
                  </div>
                  <div className="bg-slate-800/40 p-3 rounded-sm">
                    <p className="text-[10px] uppercase text-slate-500">Gastado</p>
                    <p className="text-lg font-bold text-emerald-400 font-mono">{formatoMXN(detalle.cliente.total_gastado || 0)}</p>
                  </div>
                  <div className="bg-slate-800/40 p-3 rounded-sm">
                    <p className="text-[10px] uppercase text-slate-500">Premios</p>
                    <p className="text-lg font-bold text-amber-400 font-mono">{detalle.cliente.premios_canjeados || 0}</p>
                  </div>
                </div>

                {/* Vehiculos del cliente — editables */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs uppercase tracking-wider text-slate-400">Vehículos ({(detalle.vehiculos || []).length})</p>
                    <Button size="sm" variant="outline" onClick={() => {
                      setVehiculoEdit(null);
                      setFormVeh({ placa: '', tipo_vehiculo: 'auto', color: '', marca: '', modelo: '', anio: '', notas: '' });
                      setDialogVeh(true);
                    }}
                      className="border-cyan-700 text-cyan-400 hover:bg-cyan-500/10 h-7 text-xs"
                      data-testid="agregar-vehiculo-btn">
                      <Plus className="w-3 h-3 mr-1" /> Agregar
                    </Button>
                  </div>
                  <div className="space-y-1">
                    {(detalle.vehiculos || []).length === 0 ? (
                      <p className="text-xs text-slate-500 text-center py-3">Sin vehículos registrados</p>
                    ) : detalle.vehiculos.map(v => (
                      <div key={v.id} className="flex items-center justify-between bg-slate-800/30 px-3 py-2 rounded-sm" data-testid={`vehiculo-item-${v.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-slate-200 font-mono font-bold text-sm">{v.placa || 'Sin placa'}</span>
                            {v.es_principal && <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-700 text-[9px]">PRINCIPAL</Badge>}
                          </div>
                          <p className="text-[11px] text-slate-500">
                            {[v.marca, v.modelo, v.anio > 0 ? v.anio : null, v.color].filter(Boolean).join(' · ') || 'Sin datos de vehículo'}
                          </p>
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => {
                          setVehiculoEdit(v);
                          setFormVeh({
                            placa: v.placa || '', tipo_vehiculo: v.tipo_vehiculo || 'auto',
                            color: v.color || '', marca: v.marca || '',
                            modelo: v.modelo || '', anio: v.anio || '', notas: v.notas || ''
                          });
                          setDialogVeh(true);
                        }}
                          className="text-slate-500 hover:text-cyan-400 h-7 text-xs"
                          data-testid={`editar-vehiculo-${v.id}`}>
                          Editar
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Historial de visitas ({detalle.historial.length})</p>
                  <div className="space-y-1">
                    {detalle.historial.length === 0 ? (
                      <p className="text-xs text-slate-500 text-center py-4">Sin visitas registradas</p>
                    ) : detalle.historial.map(t => (
                      <div key={t.id} className="flex items-center justify-between text-sm bg-slate-800/30 px-3 py-2 rounded-sm">
                        <div className="flex items-center gap-3">
                          <span className="text-slate-500 text-xs font-mono">{t.fecha}</span>
                          <span className="text-slate-300">{t.servicio_nombre || 'Lavado'}</span>
                          {t.lealtad_premio_aplicado && (
                            <Badge className="bg-amber-500/20 text-amber-400 border-amber-700 text-[9px]">
                              <Gift className="w-2.5 h-2.5 mr-0.5 inline" /> Premio
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-slate-500">{t.numero_turno}</span>
                          <span className="text-cyan-400 font-mono text-xs">{formatoMXN(t.precio_final ?? t.precio)}</span>
                          {t.pagado ? (
                            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-700 text-[9px]">Pagado</Badge>
                          ) : (
                            <Badge className="bg-slate-700/40 text-slate-400 border-slate-700 text-[9px]">Sin cobrar</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Nuevo cliente */}
      <Dialog open={dialogNuevo} onOpenChange={setDialogNuevo}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="dialog-nuevo-cliente">
          <DialogHeader>
            <DialogTitle className="font-barlow text-slate-100 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-cyan-400" /> Nuevo Cliente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Nombre *</Label>
              <Input value={form.nombre}
                onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                placeholder="Nombre completo"
                className="bg-slate-950 border-slate-700 text-slate-200"
                data-testid="input-nombre-cliente" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Telefono * (10 digitos)</Label>
              <Input value={form.telefono}
                onChange={e => setForm(f => ({ ...f, telefono: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                placeholder="5551234567"
                className="bg-slate-950 border-slate-700 text-slate-200 font-mono"
                data-testid="input-telefono-cliente" />
              {form.telefono.length > 0 && form.telefono.length < 10 && (
                <p className="text-[10px] text-amber-400 mt-1">Faltan {10 - form.telefono.length} digitos</p>
              )}
            </div>
            <div className="pt-2 border-t border-slate-800">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Vehiculo (opcional)</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-slate-400">Placa</Label>
                  <Input value={form.placa}
                    onChange={e => setForm(f => ({ ...f, placa: e.target.value.toUpperCase() }))}
                    placeholder="ABC-123"
                    className="bg-slate-950 border-slate-700 text-slate-200 font-mono uppercase"
                    data-testid="input-placa-cliente" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Tipo</Label>
                  <select value={form.tipo_vehiculo}
                    onChange={e => setForm(f => ({ ...f, tipo_vehiculo: e.target.value }))}
                    className="w-full h-10 bg-slate-950 border border-slate-700 text-slate-200 rounded-md px-2 text-sm">
                    <option value="auto">Auto</option>
                    <option value="camioneta">Camioneta</option>
                    <option value="suv">SUV</option>
                    <option value="moto">Moto</option>
                    <option value="camion">Camion</option>
                  </select>
                </div>
              </div>
              <div className="mt-2">
                <Label className="text-xs text-slate-400">Color</Label>
                <Input value={form.color}
                  onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                  placeholder="Blanco, Negro, etc"
                  className="bg-slate-950 border-slate-700 text-slate-200" />
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <div>
                  <Label className="text-xs text-slate-400">Marca</Label>
                  <Input value={form.marca}
                    onChange={e => setForm(f => ({ ...f, marca: e.target.value }))}
                    placeholder="Nissan"
                    className="bg-slate-950 border-slate-700 text-slate-200"
                    data-testid="input-marca-cliente" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Modelo</Label>
                  <Input value={form.modelo}
                    onChange={e => setForm(f => ({ ...f, modelo: e.target.value }))}
                    placeholder="Versa"
                    className="bg-slate-950 border-slate-700 text-slate-200"
                    data-testid="input-modelo-cliente" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Año</Label>
                  <Input type="number" value={form.anio} min="1980" max="2100"
                    onChange={e => setForm(f => ({ ...f, anio: e.target.value }))}
                    placeholder="2022"
                    className="bg-slate-950 border-slate-700 text-slate-200 font-mono"
                    data-testid="input-anio-cliente" />
                </div>
              </div>
              <p className="text-[10px] text-slate-500 mt-1">Marca, modelo y año son opcionales. Puedes editarlos después desde el detalle del cliente.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Notas (opcional)</Label>
              <Input value={form.notas}
                onChange={e => setForm(f => ({ ...f, notas: e.target.value }))}
                placeholder="Preferencias, alergias, etc"
                className="bg-slate-950 border-slate-700 text-slate-200" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogNuevo(false)} className="text-slate-400">Cancelar</Button>
            <Button onClick={crearCliente} disabled={creando}
              className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold"
              data-testid="confirmar-nuevo-cliente-btn">
              {creando ? 'Creando...' : 'Crear cliente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Editar / Agregar vehículo */}
      <Dialog open={dialogVeh} onOpenChange={setDialogVeh}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="dialog-vehiculo">
          <DialogHeader>
            <DialogTitle className="font-barlow text-slate-100 flex items-center gap-2">
              <Car className="w-5 h-5 text-cyan-400" />
              {vehiculoEdit ? 'Editar vehículo' : 'Agregar vehículo'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-slate-400">Placa {!vehiculoEdit && '*'}</Label>
                <Input value={formVeh.placa}
                  onChange={e => setFormVeh(f => ({ ...f, placa: e.target.value.toUpperCase() }))}
                  placeholder="ABC-123"
                  className="bg-slate-950 border-slate-700 text-slate-200 font-mono uppercase"
                  data-testid="veh-placa-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Tipo</Label>
                <select value={formVeh.tipo_vehiculo}
                  onChange={e => setFormVeh(f => ({ ...f, tipo_vehiculo: e.target.value }))}
                  className="w-full h-10 bg-slate-950 border border-slate-700 text-slate-200 rounded-md px-2 text-sm">
                  <option value="auto">Auto</option>
                  <option value="camioneta">Camioneta</option>
                  <option value="suv">SUV</option>
                  <option value="moto">Moto</option>
                  <option value="camion">Camión</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs text-slate-400">Marca</Label>
                <Input value={formVeh.marca}
                  onChange={e => setFormVeh(f => ({ ...f, marca: e.target.value }))}
                  placeholder="Nissan"
                  className="bg-slate-950 border-slate-700 text-slate-200"
                  data-testid="veh-marca-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Modelo</Label>
                <Input value={formVeh.modelo}
                  onChange={e => setFormVeh(f => ({ ...f, modelo: e.target.value }))}
                  placeholder="Versa"
                  className="bg-slate-950 border-slate-700 text-slate-200"
                  data-testid="veh-modelo-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Año</Label>
                <Input type="number" value={formVeh.anio} min="1980" max="2100"
                  onChange={e => setFormVeh(f => ({ ...f, anio: e.target.value }))}
                  placeholder="2022"
                  className="bg-slate-950 border-slate-700 text-slate-200 font-mono"
                  data-testid="veh-anio-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Color</Label>
              <Input value={formVeh.color}
                onChange={e => setFormVeh(f => ({ ...f, color: e.target.value }))}
                placeholder="Blanco, Negro, etc"
                className="bg-slate-950 border-slate-700 text-slate-200" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Notas</Label>
              <Input value={formVeh.notas}
                onChange={e => setFormVeh(f => ({ ...f, notas: e.target.value }))}
                placeholder="Detalles del vehículo"
                className="bg-slate-950 border-slate-700 text-slate-200" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            {vehiculoEdit && (
              <Button variant="ghost" onClick={() => eliminarVehiculo(vehiculoEdit.id)}
                className="text-red-400 hover:bg-red-500/10 mr-auto"
                data-testid="eliminar-vehiculo-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
              </Button>
            )}
            <Button variant="ghost" onClick={() => setDialogVeh(false)} className="text-slate-400">Cancelar</Button>
            <Button onClick={guardarVehiculo} disabled={guardandoVeh}
              className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold"
              data-testid="guardar-vehiculo-btn">
              {guardandoVeh ? 'Guardando...' : (vehiculoEdit ? 'Actualizar' : 'Agregar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
